import { apiClient, ApiError } from "@/lib/apiClient";
import {
  BrowseAssetsResponse,
  AssetDetails,
  CreateFolderRequest,
  FileSystemItem,
} from "@/types/fileManager";

export class FileManagerService {
  static async browseAssets(path: string = ""): Promise<BrowseAssetsResponse> {
    try {
      const params: Record<string, string> = {};
      if (path) {
        params.path = path;
      }

      const response = await apiClient.get<{
        currentPath: string;
        items: FileSystemItem[];
        totalItems: number;
      }>("/api/v1/admin/:storeId/assets/browse", params);

      const responseData = response.data as {
        currentPath: string;
        items?: FileSystemItem[];
        totalItems: number;
      };

      // Ensure items is always an array to prevent null/undefined errors
      if (responseData && responseData.items) {
        responseData.items = Array.isArray(responseData.items)
          ? responseData.items
          : [];
      }

      return {
        data: responseData as {
          currentPath: string;
          items: FileSystemItem[];
          totalItems: number;
        },
        message: response.message,
        valid: response.valid,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, "NETWORK_ERROR", "Network error occurred");
    }
  }

  static async getAssetById(assetId: string): Promise<AssetDetails> {
    try {
      const response = await apiClient.get<AssetDetails>(
        `/api/v1/admin/:storeId/assets/by-id/${assetId}`,
      );
      return response.data as AssetDetails;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, "NETWORK_ERROR", "Network error occurred");
    }
  }

  // Folders are client-side only - they don't exist until files are uploaded to them
  // This method is kept for compatibility but doesn't make any API calls
  static async createFolder(_request: CreateFolderRequest): Promise<void> {
    // No-op: Folders are virtual and only exist when files are uploaded
    return Promise.resolve();
  }

  static async getUploadUrl(
    filename: string,
    path: string = "",
  ): Promise<{ uploadUrl: string; assetId: string }> {
    console.log("🔧 FileManagerService.getUploadUrl called", {
      filename,
      path,
    });

    try {
      const params: Record<string, string> = {
        filename,
      };
      if (path) {
        params.path = path;
      }

      const response = await apiClient.get<{
        upload_url: string;
        uploaded_file_name: string;
        asset_id: string;
      }>("/api/v1/admin/:storeId/assets/upload", params);

      console.log("📋 getUploadUrl response data:", response);

      const responseData = response.data as {
        upload_url?: string;
        uploaded_file_name?: string;
        asset_id?: string;
      };

      // Check if the response has the expected structure
      if (!response || !response.valid) {
        console.error("❌ Invalid or failed response:", response);
        throw new ApiError(
          500,
          "INVALID_RESPONSE",
          "Invalid response from upload URL endpoint",
        );
      }

      if (!responseData || !responseData.upload_url) {
        console.error("❌ Missing upload_url in response:", response);
        throw new ApiError(
          500,
          "MISSING_UPLOAD_URL",
          "No upload URL found in response",
        );
      }

      // Return the expected structure
      return {
        uploadUrl: responseData.upload_url,
        assetId: responseData.asset_id || "unknown",
      };
    } catch (error) {
      console.error("❌ getUploadUrl error:", error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, "NETWORK_ERROR", "Network error occurred");
    }
  }

  static async uploadFile(uploadUrl: string, file: File): Promise<void> {
    try {
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: await file.arrayBuffer(),
      });

      if (!response.ok) {
        throw new ApiError(
          response.status,
          "UPLOAD_FAILED",
          "Failed to upload file",
        );
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, "NETWORK_ERROR", "Network error occurred");
    }
  }

  static async completeAssetUpload(assetId: string): Promise<AssetDetails> {
    console.log("🔧 FileManagerService.completeAssetUpload called", {
      assetId,
    });

    try {
      const response = await apiClient.post<AssetDetails>(
        "/api/v1/admin/:storeId/assets/complete",
        {
          asset_id: assetId,
        },
      );

      console.log("📋 completeAssetUpload response:", response);

      if (!response || !response.valid) {
        console.error("❌ Invalid or failed response:", response);
        throw new ApiError(
          500,
          "INVALID_RESPONSE",
          "Invalid response from complete asset endpoint",
        );
      }

      return response.data as AssetDetails;
    } catch (error) {
      console.error("❌ completeAssetUpload error:", error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, "NETWORK_ERROR", "Network error occurred");
    }
  }

  static async deleteAsset(path: string): Promise<void> {
    try {
      const url = `/api/v1/admin/:storeId/assets?path=${encodeURIComponent(path)}`;
      await apiClient.delete(url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, "NETWORK_ERROR", "Network error occurred");
    }
  }
}
